export interface IConfiguration {
  BASE_URL: string;
  ENABLE_TIMELINE: boolean;
  ENABLE_ROTATION: boolean;
  ENABLE_MEDIA: boolean;
  ENABLE_FAQ: boolean;
  ENABLE_DOCUMENTATION: boolean;
  ENABLE_FLOORS: boolean;
  ENABLE_ABOUT: boolean;
  ENABLE_ANIMATIONS: boolean;
  ROTATION_OFFSET: number;
  TITLE: string;
  SUBTITLE: string;
  MARZIPANO_MOUSE_VIEW_MODE: 'drag' | 'qtvr';
  SIDENAV_LOGO: string;
  SIDENAV_SUBTITLE: string;
  UQ_LOGO: string;
  ANIMATION: string;
  ANIMATION_TITLE: string;
  MINIMAP_IMAGE: string;
  MINIMAP_IMAGE_LARGE: string;
  X_PIXEL_OFFSET: number;
  Y_PIXEL_OFFSET: number;
  X_SCALE: number;
  Y_SCALE: number;
  XY_FLIPPED: boolean;
  IMG_WIDTH: number;
  IMG_HEIGHT: number;
  INITIAL_DATE: string;
  INITIAL_PANO_ID: string;
  INITIAL_FLOOR: number;
  INITIAL_YAW: number;
  INITIAL_PITCH: number;
  INITIAL_FOV: number;
  BACKGROUND_IMAGE: string;
  FLOORS: number;
}

/**
 * Maps `Configuration` interface types to `_expectedConfig`.
 * Makes sure that static types provided by Typescript and runtime
 * types defined in `_expectedConfig` are consistent with each other.
 */
type MappedConfigType<I, K extends keyof I> = {
  type: I[K] extends number
    ? 'number'
    : I[K] extends string
    ? 'string'
    : I[K] extends Date
    ? 'Date'
    : I[K] extends boolean
    ? 'boolean'
    : never;
};

/**
 * Contains the .env variables are expected in the .env file and their respective types
 * Used for runtime configuration type checking
 * E.g. If PORT is set as `abc`, this will help identify that error
 */
const _expectedConfig: { [key in keyof IConfiguration]: MappedConfigType<IConfiguration, key> } = {
  BASE_URL: { type: 'string' },
  ENABLE_TIMELINE: { type: 'boolean' },
  ENABLE_ROTATION: { type: 'boolean' },
  ENABLE_MEDIA: { type: 'boolean' },
  ENABLE_FAQ: { type: 'boolean' },
  ENABLE_DOCUMENTATION: { type: 'boolean' },
  ENABLE_FLOORS: { type: 'boolean' },
  ENABLE_ABOUT: { type: 'boolean' },
  ENABLE_ANIMATIONS: { type: 'boolean' },
  ROTATION_OFFSET: { type: 'number' },
  TITLE: { type: 'string' },
  SUBTITLE: { type: 'string' },
  MARZIPANO_MOUSE_VIEW_MODE: { type: 'string' },
  SIDENAV_LOGO: { type: 'string' },
  SIDENAV_SUBTITLE: { type: 'string' },
  UQ_LOGO: { type: 'string' },
  ANIMATION: { type: 'string' },
  ANIMATION_TITLE: { type: 'string' },
  MINIMAP_IMAGE: { type: 'string' },
  MINIMAP_IMAGE_LARGE: { type: 'string' },
  X_PIXEL_OFFSET: { type: 'number' },
  Y_PIXEL_OFFSET: { type: 'number' },
  X_SCALE: { type: 'number' },
  Y_SCALE: { type: 'number' },
  XY_FLIPPED: { type: 'boolean' },
  IMG_WIDTH: { type: 'number' },
  IMG_HEIGHT: { type: 'number' },
  INITIAL_DATE: { type: 'string' },
  INITIAL_PANO_ID: { type: 'string' },
  INITIAL_FLOOR: { type: 'number' },
  INITIAL_YAW: { type: 'number' },
  INITIAL_PITCH: { type: 'number' },
  INITIAL_FOV: { type: 'number' },
  BACKGROUND_IMAGE: { type: 'string' },
  FLOORS: { type: 'number' },
};

/**
 *
 * @param key name of the config variable
 * @param actualValue value parsed from the .env file / custom
 */
function testIfConfigTypeMatches(key: keyof IConfiguration, actualValue: string | undefined): boolean {
  if (!_expectedConfig || !_expectedConfig[key] || !_expectedConfig[key].type || !key || !actualValue) return false;

  switch (_expectedConfig[key].type) {
    case 'number':
      return typeof parseFloat(actualValue) === _expectedConfig[key].type;
    case 'string':
      return typeof actualValue === _expectedConfig[key].type;
    case 'boolean':
      return typeof Boolean(JSON.parse(actualValue)) === _expectedConfig[key].type;
    // case 'Date':
    //   return new Date(actualValue) instanceof Date;
    default:
      return false;
  }
}

/**
 * Returns true if config is valid
 * @param parsedConfig Configuration object parsed by dotenv
 * @throws If config is invalid
 */
const isConfigValid = (): boolean => {
  return (Object.keys(_expectedConfig) as Array<keyof IConfiguration>).every(config => {
    if (!testIfConfigTypeMatches(config, process.env['REACT_APP_' + config])) {
      console.log(process.env['REACT_APP_' + config]);
      console.error(`Config error: ${config} does not match expected type ${_expectedConfig[config].type}`);
      return false;
    }

    return true;
  });
};

const generateConfigObject = (): IConfiguration => {
  return Object.keys(_expectedConfig).reduce((compiledConfig, configKey) => {
    const _expectedConfigObject = _expectedConfig[configKey as keyof IConfiguration];
    if (!_expectedConfigObject || !_expectedConfigObject.type) return compiledConfig;

    const value = process.env['REACT_APP_' + configKey];
    if (!value) throw new Error(`${configKey} variable does not exist in environment`);

    switch (_expectedConfigObject.type) {
      case 'number':
        return Object.assign(compiledConfig, { [configKey]: parseFloat(value) });
      case 'string':
        return Object.assign(compiledConfig, { [configKey]: value });
      case 'boolean':
        return Object.assign(compiledConfig, {
          [configKey]: Boolean(JSON.parse(value)),
        });
      // case 'Date':
      //   return Object.assign(compiledConfig, { [configKey]: new Date(value) });
      default:
        return compiledConfig;
    }
  }, {} as IConfiguration);
};

/**
 * Return the configuration object as loaded from the `.env` file
 * @throws Will throw an error if .env file could not be loaded / contains errors / missing values
 */
export const loadConfiguration = (): IConfiguration => {
  if (!isConfigValid()) throw new Error('Configuration error => .env file');
  return generateConfigObject();
};

const config = loadConfiguration();

export default config;
